import { collection, deleteField, doc, getDocs, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { storage } from '../../../shared/storage/firebase';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { ManagedCurriculum } from '../managedCurricula';
import type { CurriculumResource } from '../adapters/types';

export async function listManagedCurricula(): Promise<ManagedCurriculum[]> {
  const snap = await getDocs(collection(db, 'curricula'));
  return snap.docs.map((item) => ({ id: item.id, ...item.data() } as ManagedCurriculum));
}

export async function saveManagedCurriculum(curriculum: ManagedCurriculum): Promise<void> {
  const isDemoExperience = curriculum.kind === 'demo_experience';
  await setDoc(doc(db, 'curricula', curriculum.id), {
    title: curriculum.title,
    description: curriculum.description || '',
    publicBlurb: isDemoExperience ? deleteField() : curriculum.publicBlurb || '',
    publicBadgeUrl: isDemoExperience ? deleteField() : curriculum.publicBadgeUrl || '',
    published: curriculum.published !== false,
    kind: curriculum.kind || 'curriculum',
    durationHours: curriculum.durationHours || null,
    durationMinHours: curriculum.durationMinHours ?? null,
    durationMaxHours: curriculum.durationMaxHours ?? null,
    demoCategory: curriculum.demoCategory || null,
    referencePdf: curriculum.referencePdf || null,
    levels: curriculum.levels,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function saveCurriculumPublicMetadata(curriculum: ManagedCurriculum): Promise<void> {
  await setDoc(doc(db, 'curricula', curriculum.id), {
    title: curriculum.title,
    description: curriculum.description || '',
    publicBlurb: curriculum.publicBlurb || '',
    publicBadgeUrl: curriculum.publicBadgeUrl || '',
    published: curriculum.published !== false,
    kind: 'curriculum',
    contentSource: 'code',
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export function subscribeManagedCurriculum(
  id: string,
  onValue: (curriculum: ManagedCurriculum | null) => void,
  onError?: (error: Error) => void
) {
  return onSnapshot(doc(db, 'curricula', id), (snap) => {
    onValue(snap.exists() ? ({ id: snap.id, ...snap.data() } as ManagedCurriculum) : null);
  }, onError);
}

export async function uploadCurriculumFile(args: {
  curriculumId: string;
  levelId: string;
  file: File;
  kind: 'video' | 'worksheet' | 'resource' | 'reference-pdf' | 'student-artifact-example' | 'ai-generated-example' | 'public-badge';
  scopeId?: string;
}): Promise<CurriculumResource> {
  const safeName = args.file.name.replace(/[^a-zA-Z0-9._-]+/g, '-');
  const safeScope = args.scopeId?.replace(/[^a-zA-Z0-9._-]+/g, '-') || '';
  const uploadId = Date.now();
  const path = `curricula/${args.curriculumId}/classes/${args.levelId}/${args.kind}/${safeScope ? `${safeScope}-` : ''}${uploadId}-${safeName}`;
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, args.file, { contentType: args.file.type || 'application/octet-stream' });
  return {
    id: `${args.curriculumId}-${args.levelId}-${safeScope ? `${safeScope}-` : ''}${args.kind}-${uploadId}`,
    title: args.file.name,
    url: await getDownloadURL(fileRef),
    kind: args.kind === 'ai-generated-example' && args.file.type.startsWith('audio/')
      ? 'audio'
      : args.kind === 'resource'
      ? 'download'
      : args.kind === 'reference-pdf' || args.kind === 'student-artifact-example' || args.kind === 'ai-generated-example' || args.kind === 'public-badge'
        ? 'document'
        : args.kind,
  };
}

/** Import independent media copies; publish the example only after all uploads succeed. */
export async function uploadCurriculumShotBatch(args: { curriculumId: string; levelId: string; file: File; onProgress: (message: string) => void }): Promise<CurriculumResource> {
  const { unpackShotBatch } = await import('../shotBatch');
  const { batch, files } = await unpackShotBatch(args.file);
  const scopeId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const uploadedUrls: string[] = [];
  try {
    for (const [index, shot] of batch.shots.entries()) {
      for (const option of shot.options) {
        if (option.type === 'link') continue;
        args.onProgress(`Uploading shot ${index + 1} of ${batch.shots.length}…`);
        const media = new File([files.get(option.source)!], option.source.replace('/', '-'), { type: option.mime });
        const uploaded = await uploadCurriculumFile({ ...args, file: media, kind: 'ai-generated-example', scopeId });
        uploadedUrls.push(uploaded.url);
        option.source = uploaded.url;
      }
    }
    args.onProgress('Saving shot batch…');
    const manifest = new File([JSON.stringify(batch)], 'shot-batch.json', { type: 'application/json' });
    const uploaded = await uploadCurriculumFile({ ...args, file: manifest, kind: 'resource', scopeId });
    return { ...uploaded, title: `${batch.shots.length} shots · Our Rough Cut: Shot Selection`, kind: 'shot-batch' };
  } catch (error) {
    await Promise.allSettled(uploadedUrls.map(url => deleteObject(ref(storage, url))));
    throw error;
  }
}
